import { getEnv } from "#app/lib/env";

/**
 * Loader for Google Identity Services (GIS) — the client half of Google
 * sign-in. The app renders its own library-styled button; pressing it opens
 * GIS's popup **code flow** (`initCodeClient`), which hands back a one-shot
 * authorization code that the auth service exchanges and verifies at
 * POST /oAuth/google. This is a separate bootstrap from the Maps JS API
 * (`@vis.gl/react-google-maps`); the two never race because auth screens
 * don't mount the map (see lib/hooks/use-address-autocomplete.ts history).
 */

export const GOOGLE_CLIENT_ID = getEnv("EXPO_PUBLIC_CLIENT_ID");

interface GoogleCodeResponse {
  /** One-shot authorization code. Exchanged server-side, never decoded here. */
  code: string;
}

interface GoogleCodeError {
  type?: string;
  message?: string;
}

interface GoogleCodeClient {
  requestCode(): void;
}

export interface GoogleOAuth2Api {
  initCodeClient(config: {
    client_id: string;
    scope: string;
    ux_mode: "popup";
    callback: (response: GoogleCodeResponse) => void;
    error_callback?: (error: GoogleCodeError) => void;
  }): GoogleCodeClient;
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

function getApi(): GoogleOAuth2Api | null {
  return (
    (window as { google?: { accounts?: { oauth2?: GoogleOAuth2Api } } }).google?.accounts
      ?.oauth2 ?? null
  );
}

let loadPromise: Promise<GoogleOAuth2Api | null> | null = null;

/**
 * Resolves the GIS OAuth2 API, injecting the script on first call. Resolves
 * null — never rejects — when the feature is unconfigured (no client id) or
 * the script cannot load (offline, blocked); callers surface a soft error.
 */
export function loadGoogleIdentity(): Promise<GoogleOAuth2Api | null> {
  if (!GOOGLE_CLIENT_ID || typeof document === "undefined") return Promise.resolve(null);
  loadPromise ??= new Promise((resolve) => {
    const existing = getApi();
    if (existing) {
      resolve(existing);
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => resolve(getApi());
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return loadPromise;
}
